import Link from "next/link";
import { getLocalizedPathname, type Locale } from "@/lib/i18n";

interface BlogCardProps {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly date: string;
  readonly readingTime: string;
  readonly labels: readonly string[];
  readonly heroImage?: string;
  readonly thumbnailImage?: string;
  readonly locale?: Locale;
  readonly className?: string;
}

export function BlogCard({
  slug,
  title,
  description,
  date,
  readingTime,
  labels,
  heroImage,
  thumbnailImage,
  locale = "en",
  className = ""
}: BlogCardProps) {
  const cardImage = thumbnailImage || heroImage;
  const postHref = getLocalizedPathname(`/blog/${slug}/`, locale);

  return (
    <article
      className={`bg-card rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden ${className}`}
    >
      {cardImage && (
        <img
          src={cardImage}
          alt=""
          className="w-full h-48 object-cover"
          loading="lazy"
          width={800}
          height={448}
          decoding="async"
        />
      )}
      <div className="p-6">
        <h2 className="text-xl font-bold mb-2 leading-tight">
          <Link
            href={postHref}
            prefetch={false}
            className="text-neutral-emphasis hover:text-primary transition-colors duration-200"
          >
            {title}
          </Link>
        </h2>

        <div className="text-sm text-neutral-muted mb-3 flex items-center gap-2">
          <time dateTime={date}>{date}</time>
          <span>•</span>
          <span>{readingTime}</span>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {labels.map((label) => (
            <span
              key={label}
              className="bg-neutral-surface text-neutral-emphasis px-2 py-0.5 rounded-full text-xs font-medium border border-neutral-border"
            >
              {label}
            </span>
          ))}
        </div>

        <p className="text-neutral-muted mb-4 leading-relaxed line-clamp-2 text-sm">
          {description}
        </p>

        <Link
          href={postHref}
          prefetch={false}
          className="inline-flex items-center text-primary font-semibold hover:text-primary/80 transition-colors duration-200 group text-sm"
        >
          Read more
          <svg
            className="ml-1 w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </article>
  );
}
